/**
 * O protocolo do caso como card colapsado (E78.F3.T06).
 *
 * Antes, o formulário de oito seções ficava sempre aberto — e, no caso real,
 * repetido em cada uma das cinco peças. Agora o que fica na tela é o resumo do
 * que a plataforma declarou, em quatro colunas, mais o que ela não informou; o
 * formulário só reaparece quando o escritório clica em "Editar protocolo", e é
 * o MESMO `ProtocoloForm` — sem segunda tela de transcrição a manter.
 *
 * As duas verificações moram aqui, e não dentro do formulário, porque precisam
 * estar alcançáveis com ele fechado. Elas continuam travadas enquanto há
 * alteração não salva: verificar ali rodaria o motor sobre o protocolo
 * anterior. "Atualizar fontes externas" segue separada e discreta — é a única
 * ação que gasta consulta paga, e o aviso de custo fica visível, não em dica.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Blueprint, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type {
  FormatoDaData,
  ProtocoloAssinaturaCasoData,
  ProtocoloAssinaturaData,
} from '@/lib/exame_eletronico';
import { resumoDoProtocolo, type ResumoDoProtocolo } from '@/lib/exame_eletronico_resumo';

import { ProtocoloForm, type ProtocoloFormAcao } from './ProtocoloForm';

const t = ptBR.exameEletronico;
const tr = ptBR.exameEletronico.resumoProtocolo;
const tf = ptBR.exameEletronico.form;
const tp = ptBR.exameEletronico.tabelaDePecas;
const tl = ptBR.exameEletronico.leituraPorImagem;

/** E101.F5: de onde vieram os campos abertos, ou por que ainda não há sugestão. */
function AvisoDaLeituraPorImagem({
  semProtocolo,
  aplicada,
  lendo,
}: {
  semProtocolo: boolean;
  aplicada: boolean;
  lendo: boolean | undefined;
}): JSX.Element | null {
  if (!semProtocolo || (!aplicada && lendo !== true)) return null;
  return (
    <p
      className="border border-accent-200 bg-accent-bg px-sc-3 py-sc-2 text-body-sm text-accent-800"
      role="status"
      data-testid={aplicada ? 'protocolo-sugestao-por-imagem' : 'protocolo-sugestao-lendo'}
    >
      {aplicada ? tl.sugestaoAplicada : tl.sugestaoLendo}
    </p>
  );
}

function Colunas({ resumo }: { resumo: ResumoDoProtocolo }): JSX.Element {
  return (
    <div
      className="grid gap-sc-4 sm:grid-cols-2 lg:grid-cols-4"
      data-testid="protocolo-resumo-colunas"
    >
      {resumo.colunas.map((coluna) => (
        <div key={coluna.chave} data-testid={`protocolo-resumo-${coluna.chave}`}>
          <h3 className="font-titulo text-[13px] uppercase tracking-[0.06em] text-fg-muted">
            {coluna.rotulo}
          </h3>
          <ul className="mt-sc-1 space-y-sc-1 text-body-sm text-fg">
            {coluna.linhas.map((linha) => (
              <li key={linha}>{linha}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function Ausencias({ ausencias }: { ausencias: readonly string[] }): JSX.Element {
  return (
    <p className="text-body-sm text-fg-muted" data-testid="protocolo-resumo-ausencias">
      <span className="text-fg-dim">{tr.ausenciasTitulo}:</span>{' '}
      {ausencias.length === 0 ? tr.ausenciasVazio : ausencias.join(' · ')}
    </p>
  );
}

/** Rótulo do botão: gira quando a ação correspondente está em voo. */
function Rotulo({ girando, texto }: { girando: boolean; texto: string }): JSX.Element {
  if (!girando) return <>{texto}</>;
  return (
    <>
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      {texto}
    </>
  );
}

function Cabecalho({
  resumo,
  semTitulo,
}: {
  resumo: ResumoDoProtocolo | null;
  semTitulo: boolean;
}): JSX.Element {
  return (
    <header>
      {semTitulo ? null : (
        <h2 id="protocolo-do-caso-titulo" className="text-display-sm text-fg">
          {t.protocoloDoCasoTitulo}
        </h2>
      )}
      {resumo === null ? (
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{tr.semProtocolo}</p>
      ) : (
        <>
          <p className="mt-1 text-body-md text-fg" data-testid="protocolo-resumo-cabecalho">
            {resumo.cabecalho}
          </p>
        </>
      )}
    </header>
  );
}

interface AcoesProps {
  temResumo: boolean;
  editando: boolean;
  emVoo: boolean;
  concluido: boolean;
  podeVerificar: boolean;
  rotuloVerificar: string;
  acaoEmCurso: ProtocoloFormAcao | null;
  onAlternarEdicao: () => void;
  /** Sem ele, o card não tem "Verificar" (E203.F5.T02: salvar já verifica de novo). */
  onVerificar?: (() => void) | undefined;
  onAtualizarConsultas: () => void;
}

function Acoes({
  temResumo,
  editando,
  emVoo,
  concluido,
  podeVerificar,
  rotuloVerificar,
  acaoEmCurso,
  onAlternarEdicao,
  onVerificar,
  onAtualizarConsultas,
}: AcoesProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-sc-3 border-t border-border pt-sc-4">
      {temResumo ? (
        <Button
          type="button"
          variant="secondary"
          onClick={onAlternarEdicao}
          disabled={emVoo || concluido}
          data-testid="protocolo-editar"
        >
          {editando ? tr.fecharEdicao : tr.editar}
        </Button>
      ) : null}
      {onVerificar !== undefined ? (
        <Button
          type="button"
          disabled={!podeVerificar}
          onClick={onVerificar}
          data-testid="protocolo-verificar"
        >
          <Rotulo
            girando={acaoEmCurso === 'verificando'}
            texto={acaoEmCurso === 'verificando' ? tf.verificando : rotuloVerificar}
          />
        </Button>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        disabled={!podeVerificar}
        onClick={onAtualizarConsultas}
        data-testid="protocolo-atualizar-consultas"
      >
        <Rotulo
          girando={acaoEmCurso === 'atualizando'}
          texto={acaoEmCurso === 'atualizando' ? tf.atualizandoConsultas : tf.atualizarConsultas}
        />
      </Button>
    </div>
  );
}

interface CorpoProps {
  editando: boolean;
  resumo: ResumoDoProtocolo | null;
  paraEditar: ProtocoloAssinaturaCasoData | null;
  /** E84.F9 (CA02) — o formato que o catálogo propôs nesta importação. */
  formatoDoCatalogo?: FormatoDaData;
  acaoEmCurso: ProtocoloFormAcao | null;
  concluido: boolean;
  onAlterado: () => void;
  onSalvar: (protocolo: ProtocoloAssinaturaData) => void;
  onVerificar?: (() => void) | undefined;
  onAtualizarConsultas: () => void;
}

/** O miolo do card: o formulário aberto, ou o resumo colapsado. */
function CorpoDoCard({
  editando,
  resumo,
  paraEditar,
  formatoDoCatalogo,
  acaoEmCurso,
  concluido,
  onAlterado,
  onSalvar,
  onVerificar,
  onAtualizarConsultas,
}: CorpoProps): JSX.Element | null {
  if (editando) {
    return (
      <ProtocoloForm
        // Remonta ao recarregar E ao chegar sugestão nova: o estado inicial
        // passa a ser o que se propõe, sobre o que já estava gravado.
        key={JSON.stringify(paraEditar)}
        idPrefix="protocolo-caso"
        protocoloInicial={paraEditar}
        acaoEmCurso={acaoEmCurso}
        concluido={concluido}
        {...(formatoDoCatalogo === undefined ? {} : { formatoDoCatalogo })}
        ocultarCodigoDeclarado
        mostrarAcoesDeVerificacao={false}
        onAlterado={onAlterado}
        onSalvar={onSalvar}
        onVerificar={onVerificar ?? (() => undefined)}
        onAtualizarConsultas={onAtualizarConsultas}
      />
    );
  }
  if (resumo === null) return null;
  return (
    <>
      <Colunas resumo={resumo} />
      <Ausencias ausencias={resumo.ausencias} />
    </>
  );
}

export interface ProtocoloDoCasoCardProps {
  protocolo: ProtocoloAssinaturaCasoData | null;
  /** Quantas peças o "Verificar" vai rodar de uma vez. */
  totalDePecas: number;
  acaoEmCurso: ProtocoloFormAcao | null;
  /** O perito concluiu alguma peça: nada mais pode ser alterado (ADR-0077 §2.4). */
  concluido: boolean;
  onSalvar: (protocolo: ProtocoloAssinaturaData) => void;
  /**
   * E203.F5.T02 (ADR-0146): opcional. Na mesa o card sai sem "Verificar": salvar o protocolo já
   * verifica de novo e renova as prévias (E180.F3), e "Atualizar fontes externas" fica, com o aviso
   * de custo.
   */
  onVerificar?: () => void;
  onAtualizarConsultas: () => void;
  /**
   * E88.F2 (GUS-30 RF06/CA05): o formato que o CATÁLOGO propõe para a
   * plataforma declarada. Vem do servidor junto com o caso, e não mais da
   * proveniência de uma sugestão — a importação pela tela saiu nesta Fase, e
   * sem ele o CA02 da GUS-44 perderia a origem à vista.
   */
  formatoDoCatalogo?: FormatoDaData;
  /**
   * E91.F2 (GUS-51 RF03): o card vive dentro de uma seção recolhida, que já
   * tem o título. Com o `id` desse título o card não repete o seu e se rotula
   * pelo da seção.
   */
  tituloId?: string;

  /**
   * Alguma peça tem código digitado e não salvo (E84.F7 / GUS-45 RF01).
   *
   * O card não conhece as peças — quem as tem é a página —, mas é dele o botão
   * que dispara o motor. Sem este aviso a verificação rodava sobre o código
   * GRAVADO enquanto a tabela já mostrava o digitado, e o perito lia o selo do
   * valor antigo ao lado do valor novo.
   */
  edicaoPendenteDePecas?: boolean;
  /**
   * E101.F5 (ADR-0085 §2.4): o protocolo sugerido pelos comprovantes lidos por imagem. Só abre o
   * formulário quando não há protocolo gravado — o gravado nunca é substituído — e nada é gravado
   * até o "Salvar" do perito.
   */
  sugestaoPorImagem?: ProtocoloAssinaturaCasoData | null;
  /** E101.F5: algum comprovante do caso ainda está sendo lido por imagem. */
  leituraEmAndamento?: boolean;
}

/**
 * E101.F5: a sugestão por imagem entra só sem protocolo gravado e antes de o perito digitar;
 * depois de aplicada, fica até o "Salvar" — a que chega tarde não apaga o que ele editou.
 */
function useSugestaoAplicada(
  protocolo: ProtocoloAssinaturaCasoData | null,
  alterado: boolean,
  sugestao: ProtocoloAssinaturaCasoData | null | undefined,
  abrir: () => void,
): ProtocoloAssinaturaCasoData | null {
  const [aplicada, setAplicada] = useState<ProtocoloAssinaturaCasoData | null>(null);
  const sugerida = protocolo === null && !alterado ? (sugestao ?? null) : null;
  if (sugerida !== null && JSON.stringify(sugerida) !== JSON.stringify(aplicada)) {
    setAplicada(sugerida);
    abrir();
  }
  if (protocolo !== null && aplicada !== null) setAplicada(null);
  return protocolo === null ? aplicada : null;
}

/**
 * Por que o `Verificar` está travado. Dois motivos independentes pedem dois
 * avisos: o perito precisa saber ONDE está a pendência — no protocolo do caso
 * ou numa peça da tabela.
 */
function PorQueTravado({
  precisaSalvar,
  edicaoPendenteDePecas,
  concluido,
}: {
  precisaSalvar: boolean;
  edicaoPendenteDePecas: boolean;
  concluido: boolean;
}): JSX.Element | null {
  if (concluido) return null;
  return (
    <>
      {precisaSalvar ? (
        <p className="text-caption text-fg-dim" data-testid="protocolo-verificar-hint">
          {tf.verificarHint}
        </p>
      ) : null}
      {edicaoPendenteDePecas ? (
        <p
          className="text-caption text-fg-dim"
          role="status"
          data-testid="protocolo-edicao-pendente-hint"
        >
          {tf.verificarHintPecas}
        </p>
      ) : null}
    </>
  );
}

export function ProtocoloDoCasoCard({
  protocolo,
  totalDePecas,
  acaoEmCurso,
  concluido,
  onSalvar,
  onVerificar,
  onAtualizarConsultas,
  formatoDoCatalogo,
  tituloId,
  edicaoPendenteDePecas = false,
  sugestaoPorImagem,
  leituraEmAndamento,
}: ProtocoloDoCasoCardProps): JSX.Element {
  const resumo = resumoDoProtocolo(protocolo);
  // Sem protocolo transcrito não há resumo a colapsar: a tela abre no formulário.
  const [editando, setEditando] = useState(resumo === null);
  const [alterado, setAlterado] = useState(false);
  // E88.F2: o protocolo chega GRAVADO (o comprovante do passo 2 o preenche,
  // E84.F1). Sem importação pela tela não há proposta a conferir — o que o
  // formulário edita é sempre o gravado.
  const [visto, setVisto] = useState(protocolo);
  const protocoloMudou = JSON.stringify(visto) !== JSON.stringify(protocolo);
  if (protocoloMudou) {
    setVisto(protocolo);
    setAlterado(false);
    setEditando(protocolo === null);
  }

  const aplicada = useSugestaoAplicada(protocolo, alterado, sugestaoPorImagem, () =>
    setEditando(true),
  );
  const paraEditar = protocolo ?? aplicada;

  const emVoo = acaoEmCurso !== null;
  const precisaSalvar = resumo === null || alterado;
  const podeVerificar = !precisaSalvar && !emVoo && !concluido && !edicaoPendenteDePecas;
  const rotuloVerificar =
    totalDePecas === 1
      ? tp.verificarUmaPeca
      : tp.verificarPecas.replace('{n}', String(totalDePecas));
  // E84.F9 (CA02) + E88.F2: a procedência do catálogo, quando ele conhece a
  // plataforma. Espalhada como objeto porque `exactOptionalPropertyTypes`
  // recusa `undefined` explícito.
  const doCatalogo = formatoDoCatalogo === undefined ? {} : { formatoDoCatalogo };

  return (
    <Blueprint
      as="section"
      className="space-y-sc-4 p-sc-6"
      data-testid="protocolo-do-caso"
      data-editando={editando}
      aria-labelledby={tituloId ?? 'protocolo-do-caso-titulo'}
    >
      <Cabecalho resumo={resumo} semTitulo={tituloId !== undefined} />
      <AvisoDaLeituraPorImagem
        semProtocolo={protocolo === null}
        aplicada={aplicada !== null}
        lendo={leituraEmAndamento}
      />

      <CorpoDoCard
        editando={editando}
        resumo={resumo}
        paraEditar={paraEditar}
        {...doCatalogo}
        acaoEmCurso={acaoEmCurso}
        concluido={concluido}
        onAlterado={() => setAlterado(true)}
        onSalvar={onSalvar}
        onVerificar={onVerificar}
        onAtualizarConsultas={onAtualizarConsultas}
      />

      <Acoes
        temResumo={resumo !== null}
        editando={editando}
        emVoo={emVoo}
        concluido={concluido}
        podeVerificar={podeVerificar}
        rotuloVerificar={rotuloVerificar}
        acaoEmCurso={acaoEmCurso}
        onAlternarEdicao={() => setEditando((atual) => !atual)}
        onVerificar={onVerificar}
        onAtualizarConsultas={onAtualizarConsultas}
      />
      <PorQueTravado
        precisaSalvar={precisaSalvar}
        edicaoPendenteDePecas={edicaoPendenteDePecas}
        concluido={concluido}
      />
      <p className="max-w-prose text-caption text-fg-dim" data-testid="protocolo-aviso-custo">
        {tf.atualizarConsultasHint}
      </p>
    </Blueprint>
  );
}
